import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeAutoObservable } from 'mobx';
import { AuthService } from '../Service/AuthService';
import { User } from '../Model/User';
import { AwsService } from '../Service/AwsService';
import { useConnectionStatus } from '../Components/ConnectionStatusProvider';
import AuthLoadingScreen from '../Components/AuthLoadingScreen';


// Define supported provider types
export type GitProvider = 'github' | 'gitlab' | 'bitbucket';

// Interface for MFA response
interface MFAResponse {
  requiresMFA: boolean;
  email: string;
  qai: string;
  code: string;
}

export class AuthViewModel {
  isAuthenticated = false;
  user: User | null = null;
  error = '';
  loading = false;

  constructor() {
    makeAutoObservable(this);
    const token = localStorage.getItem('authToken');
    this.isAuthenticated = !!token;
  }
  
  setError(error: string) {
    this.error = error;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  // Method to handle multiple providers - now returns MFA data if needed
  async login(provider: GitProvider): Promise<MFAResponse | null> {
    this.setLoading(true);
    try {
      switch (provider) {
        case 'github':
          const githubUrl = await AuthService.getGithubAuthUrl();
          window.location.href = githubUrl;
          return null; // Will redirect, so no return value needed
        case 'gitlab':
          // Add GitLab login logic when available
          this.setError('GitLab login not implemented yet');
          return null;
        case 'bitbucket':
          const bitbucketUrl = await AuthService.getBitbucketAuthUrl();
          window.location.href = bitbucketUrl;
          return null; // Will redirect, so no return value needed
        default:
          this.setError('Unsupported provider');
          return null;
      }
    } catch (error) {
      console.log(error);
      this.setError(`Failed to initiate ${provider} login`);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async handleGithubCallback(code: string, connectionStatus: any, installation_id?: number): Promise<MFAResponse | null> {
    this.setLoading(true);
    try {
      connectionStatus.updateStatus({ message: 'Authenticating with GitHub...', success: false });

      const response = await AuthService.handleGithubCallback(code, installation_id);

      // Redirect to GitHub App installation if not yet installed
      if (response.requires_installation && response.installation_url) {
        window.location.href = response.installation_url;
        return null;
      }

      // Check if response contains the expected MFA data
      if (response.username && response.email && response.qai !== undefined) {
        return {
          requiresMFA: true,
          email: response.email,
          qai: response.qai,
          code: code,
        };
      } else {
        throw new Error('Invalid response from GitHub authentication');
      }
    } catch (error) {
      connectionStatus.setDisconnected('GitHub authentication failed');
      this.setError(error instanceof Error ? error.message : 'Authentication failed');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async handleBitbucketCallback(code: string, connectionStatus: any): Promise<MFAResponse | null> {
    this.setLoading(true);
    try {
      connectionStatus.updateStatus({ message: 'Authenticating with Bitbucket...', success: false });
      
      const response = await AuthService.handleBitbucketCallback(code);
      
      // Check if response contains the expected MFA data
      if (response.username && response.email && response.qai !== undefined) {
        // Return MFA data for verification
        return {
          requiresMFA: true,
          email: response.email,
          qai: response.qai,
          code: code // Save the Bitbucket callback code for MFA verification
        };
      } else {
        throw new Error('Invalid response from Bitbucket authentication');
      }
    } catch (error) {
      connectionStatus.setDisconnected('Bitbucket authentication failed');
      this.setError(error instanceof Error ? error.message : 'Authentication failed');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async logout(connectionStatus: any) {
    this.setLoading(true);
    try {
      localStorage.removeItem('authToken');
      connectionStatus.setDisconnected('Logged out from AWS');
      this.isAuthenticated = false;
      window.location.href = '/';
    } catch (error) {
      this.setError('Failed to logout');
    } finally {
      this.setLoading(false);
    }
  }

  checkAuthentication(): boolean {
    const token = localStorage.getItem('authToken');
    this.isAuthenticated = !!token;
    return this.isAuthenticated;
  }

  hasProvider(provider: GitProvider): boolean {
    return true;
  }

  get isLoading(): boolean {
    return this.loading;
  }
}

export const authViewModel = new AuthViewModel();

export const useAuthViewModel = () => {
  const navigate = useNavigate();
  const connectionStatus = useConnectionStatus();

  const login = useCallback(async (provider: GitProvider) => {
    return await authViewModel.login(provider);
  }, []);

  const handleGithubCallback = useCallback(async (code: string, installation_id?:number) => {
    return await authViewModel.handleGithubCallback(code, connectionStatus, installation_id );
  }, [connectionStatus]);

  const handleBitbucketCallback = useCallback(async (code: string) => {
    return await authViewModel.handleBitbucketCallback(code, connectionStatus);
  }, [connectionStatus]);

  const logout = useCallback(() => {
    return authViewModel.logout(connectionStatus);
  }, [connectionStatus]);

  const checkAuthentication = useCallback(() => {
    return authViewModel.checkAuthentication();
  }, []);

  return {
    user: authViewModel.user,
    isLoading: authViewModel.isLoading,
    error: authViewModel.error,
    isAuthenticated: authViewModel.isAuthenticated,
    login,
    handleGithubCallback,
    handleBitbucketCallback,
    logout,
    checkAuthentication,
    hasProvider: (provider: GitProvider) => authViewModel.hasProvider(provider),
  };
};